import { apiFetch } from "./api.js";

/**
 * Fetch active personas, filtered by voter location or explicit geo params.
 * @param {object} [opts]
 * @param {string} [opts.phone]   - voter phone → API looks up their location to auto-filter
 * @param {string} [opts.country] - explicit country code filter (e.g. "NG")
 * @param {string} [opts.state]   - explicit state filter
 * @param {string} [opts.lga]     - explicit LGA filter
 */
export async function getCandidates({ country, state, lga, personaType } = {}) {
  const params = new URLSearchParams();
  if (country) params.set("country", country);
  if (state) params.set("state", state);
  if (lga) params.set("lga", lga);
  // v1.28.0 (AGCY) — type filter. Backend defaults to 'official' if omitted,
  // so the pre-existing voter directory (Directory.jsx) doesn't need to
  // pass this explicitly — calling getCandidates() with no type still
  // returns officials. The new /agencies directory passes personaType:
  // "agency" to get the agencies list.
  if (personaType) params.set("type", personaType);
  const qs = params.toString() ? `?${params}` : "";

  // EMAIL-10: voter location resolved via JWT auth_id in backend (no more X-Voter-Phone)
  return apiFetch(`/api/candidates${qs}`);
}

/** Fetch a single persona by id/slug */
export async function getCandidate(id) {
  return apiFetch(`/api/candidates/${id}`);
}

/** Fetch a single persona by slug without geo filter (public profile access) */
export async function getCandidateBySlug(slug) {
  return apiFetch(`/api/candidates/by-slug/${slug}`);
}

/**
 * CLONE-1: AI pre-fill for new persona setup (admin only).
 * Returns { bio, statement, tagline, suggestedPrompts, keyPolicies, knowledgeEntries }
 */
export async function generatePersonaDraft(name, country, role) {
  return apiFetch("/api/admin/generate-draft", {
    method: "POST",
    body: JSON.stringify({ name, country, role }),
  });
}

/**
 * CLONE-1: Save a new persona + selected KB entries to DB (admin only).
 * Embeddings generated server-side.
 * @param {object} persona - persona fields
 * @param {Array}  knowledgeEntries - selected KB draft entries
 * Returns { persona, knowledgeEntries }
 */
export async function createPersonaWithKb(persona, knowledgeEntries = []) {
  return apiFetch("/api/admin/create-persona", {
    method: "POST",
    body: JSON.stringify({ persona, knowledgeEntries }),
  });
}

/**
 * Validate persona data without creating it (slug + role/geo uniqueness + required fields).
 * Uses the same create-persona endpoint with validateOnly flag.
 * Returns { valid: true } or throws with validation error.
 */
export async function validatePersona(persona) {
  return apiFetch("/api/admin/create-persona", {
    method: "POST",
    body: JSON.stringify({ persona, validateOnly: true }),
  });
}

/**
 * CLONE-2: Update persona fields (admin only). Returns updated persona.
 * @param {string} id - persona id
 * @param {object} updates - partial persona fields
 */
export async function updateCandidate(id, updates) {
  return apiFetch(`/api/candidates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

/**
 * LAUNCH-1: Toggle persona public/private visibility (admin only).
 * @param {string} personaId
 * @param {boolean} active - true = public, false = private
 * Returns the updated persona object.
 */
export async function updatePersonaVisibility(personaId, active) {
  return apiFetch(`/api/admin/personas/${personaId}/visibility`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

/** CAND-8 + CAND-9: Fetch personas with engagement stats + pagination (admin only).
 *  v1.28.0 (AGCY): optional `personaType` filter — "official" or "agency". When
 *  omitted the backend returns BOTH types (admin overview), so the sidebar
 *  Officials / Agencies entries pass the filter explicitly. */
export async function getAdminPersonas({ limit = 20, offset = 0, personaType } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  if (personaType) params.set("type", personaType);
  return apiFetch(`/api/admin/personas?${params.toString()}`);
}

/** Fetch analytics data for a candidate */
export async function getCandidateAnalytics(personaId) {
  return apiFetch(`/api/candidates/${personaId}/analytics`);
}

/**
 * CACHE-1: Fetch per-candidate response cache stats (admin only).
 * Returns { totalEntries, totalHits, hitRate }
 */
export async function getCacheStats(personaId) {
  return apiFetch(`/api/candidates/${personaId}/cache-stats`);
}

/**
 * CACHE-1: Flush all cached responses for a persona (admin only).
 */
export async function clearPersonaCache(personaId) {
  return apiFetch(`/api/candidates/${personaId}/cache`, { method: "DELETE" });
}

/**
 * ADMIN-8: Fetch semantic question clusters for analytics (admin only).
 * Returns { clusters: [{ representative, count, avgCpScore, insight, sampleQuestions }] }
 */
export async function getQuestionClusters(personaId, limit = 20, offset = 0) {
  return apiFetch(`/api/admin/personas/${personaId}/question-clusters?limit=${limit}&offset=${offset}`);
}

/**
 * ADMIN-5: Upload avatar image for a persona (admin only).
 * Uses raw fetch because apiFetch forces Content-Type: application/json.
 * @param {string} personaId
 * @param {File} file - image file (JPEG/PNG/WebP, max 2MB)
 * Returns { photoUrl, persona }
 */
export async function uploadPersonaAvatar(personaId, file) {
  const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_BASE}/api/admin/personas/${personaId}/avatar`, {
    method: "POST",
    headers: {
      "X-API-Secret": import.meta.env.VITE_API_SECRET ?? "",
      "X-Admin-Token": sessionStorage.getItem("qm_admin_token") ?? "",
    },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Upload failed" }));
    throw new Error(err.error ?? "Upload failed");
  }
  return res.json();
}

/**
 * ADMIN-1: Fetch paginated chat sessions for a persona (admin only).
 * Returns { sessions: [...], total: number }
 */
export async function getPersonaSessions(personaId, limit = 20, offset = 0, sentiment = "") {
  const params = `limit=${limit}&offset=${offset}${sentiment ? `&sentiment=${sentiment}` : ""}`;
  return apiFetch(`/api/admin/personas/${personaId}/sessions?${params}`);
}

/**
 * ADMIN-1: Fetch messages for a specific session (admin only).
 * Returns { sessionId, maskedPhone, messages: [...], total: number }
 */
export async function getSessionMessages(personaId, sessionId, limit = 50, offset = 0) {
  return apiFetch(`/api/admin/personas/${personaId}/sessions/${sessionId}?limit=${limit}&offset=${offset}`);
}

// ── CRED-2: Credit management ─────────────────────────────────────────────

export async function getPersonaCredits(personaId) {
  return apiFetch(`/api/admin/personas/${personaId}/credits`);
}

export async function fundPersonaCredits(personaId, amount, description = "") {
  return apiFetch(`/api/admin/personas/${personaId}/credits/fund`, {
    method: "POST",
    body: JSON.stringify({ amount, description }),
  });
}

export async function getCreditBundles() {
  return apiFetch("/api/admin/credit-bundles");
}
